import { useTranslation } from 'react-i18next';
import SectionsTabs from '../../../components/SectionsTabs/SectionsTabs';
import { IProduct } from '../../../interfaces/Product';
import { Grid } from '@mantine/core';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import EmptyListResult from '../../../components/EmptyListResult/EmptyListResult';
import { Tabs } from '@mantine/core';
import { ISection } from '../../../interfaces/Section';
import ProductCard from './ProductCard';
import { OrderContext } from '../../../context/OrderContext';
import { OrderProduct } from '../interfaces';
import { useFormContext } from 'react-hook-form';
import SelectProductOptionModal from './SelectProductOptionModal';
import NewProductModal from './NewProductModal';
import { IconPlus } from '@tabler/icons-react';
import ProductType from '../../../enum/ProductType';
import {
  findMergeableCatalogLineIndex,
  generateObjectIdLikeLineId,
  isCatalogMergeableLine,
  normalizeAttributesForLineType,
  normalizeProductIdentity,
  normalizeSelectedOptionIdentity,
  resolveLineId
} from '../utils/lineIdentity';
import { initializePieceCountForNewLine, maybeRecomputePieceCount } from '../utils/pieceCount';
import { recalculateOrderProductsWithLineIds } from '../utils/recalculateOrderProducts';

interface ProductListProps {
  productSearchTerm: string;
  sections: ISection[];
  storeDiscounts: any;
  isLoadingSections: boolean;
  refetchSections?: () => void;
}

const ProductList = ({
  sections,
  productSearchTerm,
  storeDiscounts,
  isLoadingSections,
  refetchSections
}: ProductListProps) => {
  const { t } = useTranslation(['NewOrder', 'SectionsPage']);
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { setUpdatingProductModal, setUpdatingProduct, setLastAddedOrderProduct } =
    useContext(OrderContext);

  const { watch, setValue, getValues } = useFormContext();

  const products: OrderProduct[] = watch('products');
  const isExpress = watch('express');

  const isMeasuredProduct = (type?: string) =>
    type === ProductType.WEIGHT || type === ProductType.LENGTH;
  const getLineOptionId = (line: any) => normalizeSelectedOptionIdentity(line);
  const getLineProductId = (line: any) => normalizeProductIdentity(line);

  // Use functions to get latest values when needed
  const getLatestValues = () => ({
    discountCodeObj: getValues('discountCodeObj'),
    express: getValues('express'),
    customerInfo: getValues('customerInfo'),
    orderType: getValues('orderType'),
    returnItemsChargeMode: getValues('returnItemsChargeMode')
  });

  // Modal state for variant selection
  const [optionModalOpened, setOptionModalOpened] = useState(false);
  const [optionModalProduct, setOptionModalProduct] = useState<IProduct | null>(null);
  const [openedAddProductModal, setOpenedAddProductModal] = useState(false);

  // Sum quantities per productId (ignores variant) just for badge display
  const productQtyMap = useMemo(() => {
    return (products || []).reduce((acc: Record<string, number>, p: any) => {
      if (!isCatalogMergeableLine(p)) return acc;
      const key = p.productId || p._id;
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + (p.quantity || 0);
      return acc;
    }, {});
  }, [products]);

  const findProductIndexByLine = useCallback(
    (sourceProducts: OrderProduct[], targetProduct: OrderProduct): number => {
      const targetLineId = targetProduct.lineId || targetProduct.orderProductLineID;
      if (targetLineId) {
        return sourceProducts.findIndex((line) => resolveLineId(line) === targetLineId);
      }

      const targetProductId = getLineProductId(targetProduct);
      const targetOptionId = getLineOptionId(targetProduct);

      return sourceProducts.findIndex((line) => {
        return isCatalogMergeableLine(line) &&
          getLineProductId(line) === targetProductId &&
          getLineOptionId(line) === targetOptionId;
      });
    },
    []
  );

  const updateOrderProducts = useCallback(
    (newProducts: OrderProduct[]) => {
      // Get latest values at the time of update
      const { discountCodeObj, express, customerInfo, orderType, returnItemsChargeMode } =
        getLatestValues();
      const { newOrderProds, buyAndGetProds } = recalculateOrderProductsWithLineIds({
        discountCodeObj,
        products: newProducts,
        express,
        storeDiscounts,
        customerInfo,
        previousBuyAndGetProducts: getValues('buyAndGetProducts'),
        orderType,
        returnItemsChargeMode
      });
      setValue('products', newOrderProds);
      setValue('buyAndGetProducts', buyAndGetProds);
    },
    [storeDiscounts, setValue, getValues]
  );

  const removeProduct = useCallback(
    (prod: OrderProduct) => {
      if (prod.returnSourceOrderLineId) return;
      const targetLineId = resolveLineId(prod);
      const filteredProducts = (products || []).filter((product: OrderProduct) => {
        return resolveLineId(product) !== targetLineId;
      });
      updateOrderProducts(filteredProducts);
    },
    [products, updateOrderProducts]
  );

  const updateProductQuantity = useCallback(
    (product: OrderProduct, quantity: number) => {
      if (product.returnSourceOrderLineId) return;
      const prodIdx = findProductIndexByLine(products || [], product);
      if (prodIdx !== -1) {
        const productsCopy = [...(products || [])];
        if (quantity <= 0) {
          productsCopy.splice(prodIdx, 1);
          updateOrderProducts(productsCopy);
          return;
        }

        productsCopy[prodIdx] = maybeRecomputePieceCount({
          ...productsCopy[prodIdx],
          quantity,
          attributes: normalizeAttributesForLineType(productsCopy[prodIdx], quantity)
        });
        updateOrderProducts(productsCopy);
      }
    },
    [products, updateOrderProducts, findProductIndexByLine]
  );

  const addProductToOrder = useCallback(
    (
      product: IProduct,
      quantity: number,
      option?: { optionId: string; price: number; expressPrice: number; label: string }
    ) => {
      const optionId = option?.optionId || null;
      const selectedOption = optionId ? { _id: optionId, label: option?.label || '' } : null;

      const resolvedPrice = option ? option.price : product.price;
      const resolvedExpressPrice = option ? option.expressPrice : product.expressPrice;
      const resolvedName = option ? option.label : product.name;

      if (isMeasuredProduct(product.type)) {
        const lineId = generateObjectIdLikeLineId();
        setUpdatingProductModal(true);
        const sendingProductWeight: OrderProduct = initializePieceCountForNewLine({
          ...product,
          lineId,
          orderProductLineID: lineId,
          productId: product._id || '',
          selectedOptionId: optionId,
          selectedOption,
          quantity: 0,
          extraAmount: 0,
          discountAmount: 0,
          properties: [],
          attributes: [],
          price: resolvedPrice,
          expressPrice: resolvedExpressPrice,
          name: resolvedName,
          type: product.type,
          taxExemptOne: product.taxExemptOne,
          taxExemptTwo: product.taxExemptTwo,
          taxExemptThree: product.taxExemptThree
        });
        setUpdatingProduct(sendingProductWeight);
        return;
      }

      const prodIdx = findMergeableCatalogLineIndex(
        products || [],
        product._id || '',
        optionId
      );

      if (prodIdx === -1) {
        const lineId = generateObjectIdLikeLineId();
        const newProduct: OrderProduct = initializePieceCountForNewLine({
          ...product,
          lineId,
          orderProductLineID: lineId,
          productId: product._id,
          extraAmount: 0,
          discountAmount: 0,
          properties: [],
          quantity: quantity || 1,
          price: resolvedPrice,
          name: resolvedName,
          expressPrice: resolvedExpressPrice,
          type: product.type,
          taxExemptOne: product.taxExemptOne,
          taxExemptTwo: product.taxExemptTwo,
          taxExemptThree: product.taxExemptThree,
          attributes: [{}]
        });
        newProduct.selectedOptionId = optionId;
        newProduct.selectedOption = selectedOption;
        const productsCopy = [...(products || []), newProduct];
        setLastAddedOrderProduct(newProduct);
        updateOrderProducts(productsCopy);
      } else {
        const productsCopy = [...(products || [])];
        productsCopy[prodIdx] = maybeRecomputePieceCount({
          ...productsCopy[prodIdx],
          quantity: (productsCopy[prodIdx].quantity || 0) + 1,
          attributes: productsCopy[prodIdx].attributes
            ? [...productsCopy[prodIdx].attributes, {}]
            : [{}]
        });
        setLastAddedOrderProduct(productsCopy[prodIdx]);
        updateOrderProducts(productsCopy);
      }
    },
    [
      products,
      updateOrderProducts,
      setUpdatingProductModal,
      setUpdatingProduct,
      setLastAddedOrderProduct
    ]
  );

  const onProductCardClicked = useCallback(
    (product: IProduct, quantity: number) => {
      // If product has active variations, open modal
      const activeOptions = (product.priceOptions || []).filter((o) => o?.isActive !== false);
      if (activeOptions.length > 0) {
        setOptionModalProduct(product);
        setOptionModalOpened(true);
        return;
      }
      addProductToOrder(product, quantity);
    },
    [addProductToOrder]
  );

  const handleOptionConfirm = useCallback(
    (payload: { optionId: string; price: number; expressPrice: number; label: string }) => {
      if (!optionModalProduct) return;
      addProductToOrder(optionModalProduct, 1, payload);
      setOptionModalOpened(false);
      setOptionModalProduct(null);
    },
    [optionModalProduct, addProductToOrder]
  );

  const handleIncrement = useCallback(
    (product: IProduct) => {
      const activeOptions = (product.priceOptions || []).filter((o) => o?.isActive !== false);
      if (activeOptions.length > 0) {
        // force variant selection
        setOptionModalProduct(product);
        setOptionModalOpened(true);
        return;
      }
      const sumQty = productQtyMap[product._id || ''] || 0;
      if (sumQty === 0) {
        addProductToOrder(product, 1);
      } else {
        // increment the most recent no-variant line
        let line: OrderProduct | undefined;
        for (let i = (products || []).length - 1; i >= 0; i -= 1) {
          const currentLine = (products || [])[i];
          if (
            isCatalogMergeableLine(currentLine) &&
            getLineProductId(currentLine) === product._id &&
            !getLineOptionId(currentLine)
          ) {
            line = currentLine;
            break;
          }
        }

        if (line) updateProductQuantity(line as any, (line.quantity || 0) + 1);
        else addProductToOrder(product, 1);
      }
    },
    [productQtyMap, addProductToOrder, products, updateProductQuantity]
  );

  const handleSubtract = useCallback(
    (product: IProduct) => {
      // Decrement the latest matching line for simplicity
      const activeOptions = (product.priceOptions || []).filter((o) => o?.isActive !== false);
      let lineIdx = -1;
      if (activeOptions.length > 0) {
        // with variants, pick the most recent line for this product
        for (let i = (products || []).length - 1; i >= 0; i--) {
          const p: any = (products || [])[i];
          if (isCatalogMergeableLine(p) && (p.productId || p._id) === product._id) {
            lineIdx = i;
            break;
          }
        }
      } else {
        for (let i = (products || []).length - 1; i >= 0; i -= 1) {
          const currentLine: any = (products || [])[i];
          if (
            isCatalogMergeableLine(currentLine) &&
            (currentLine.productId || currentLine._id) === product._id &&
            !currentLine.selectedOptionId
          ) {
            lineIdx = i;
            break;
          }
        }
      }
      if (lineIdx !== -1) {
        const line = (products || [])[lineIdx] as OrderProduct;
        if (line.quantity <= 1) {
          removeProduct(line);
        } else {
          updateProductQuantity(line, line.quantity - 1);
        }
      }
    },
    [products, removeProduct, updateProductQuantity]
  );

  const sectionsPanels = useMemo(() => {
    if (!sections || sections.length === 0)
      return <EmptyListResult message={t('NewOrder:noSectionsAndProducts')} />;

    return sections?.map((section) => {
      const filteredProds: IProduct[] =
        (section?.products as IProduct[])?.filter((product: IProduct) => {
          if (!productSearchTerm) return true;
          const searchTerm = productSearchTerm.toLowerCase();
          return (product.name || '')?.toLowerCase().includes(searchTerm);
        }) || [];

      const AddProductCard = (
        <Grid.Col key={`add-product-${section._id}`} span={'content'} p={0}>
          <ProductCard
            key={`add-product-${section._id}`}
            sectionColor={section.boxColor}
            product={{
              _id: `add-product-${section._id}`,
              name: t('NewOrder:addProductCardTitle'),
              price: 0,
              expressPrice: 0,
              type: 'normal',
              overlayText: '',
              sku: '',
              pieces: '',
              taxExemptOne: false,
              taxExemptTwo: false,
              taxExemptThree: false,
              isActive: true,
              showInApp: false,
              owner: '',
              store: '',
              order: 999999
            }}
            onCardClicked={() => setOpenedAddProductModal(true)}
            handleSubtract={() => {}}
            handleIncrement={() => {}}
            cardIcon={
              <div
                style={{
                  height: '100%',
                  maxHeight: '60px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}>
                <IconPlus size={30} />
              </div>
            }
          />
        </Grid.Col>
      );
      return (
        <Tabs.Panel
          key={`${section._id}-${section.name}-ProductList`}
          value={section._id || ''}
          id="parentBodyContainer">
          <Grid>
            {filteredProds.map((product: IProduct) => (
              <Grid.Col key={product._id} span={'content'} p={0}>
                <ProductCard
                  key={product._id}
                  sectionColor={section.boxColor}
                  product={product}
                  productInOrder={
                    {
                      _id: product._id,
                      quantity: productQtyMap[product._id || ''] || undefined
                    } as any
                  }
                  onCardClicked={onProductCardClicked}
                  handleSubtract={handleSubtract}
                  handleIncrement={handleIncrement}
                />
              </Grid.Col>
            ))}
            {AddProductCard}
          </Grid>
          {filteredProds && filteredProds.length === 0 && (
            <EmptyListResult message={t('SectionsPage:noProductsResults')} />
          )}
        </Tabs.Panel>
      );
    });
  }, [
    sections,
    productSearchTerm,
    productQtyMap,
    onProductCardClicked,
    handleSubtract,
    handleIncrement
  ]);

  const updateTab = (value: string) => {
    const params = new URLSearchParams(location.search);
    params.set('section', value);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  return (
    <>
      <SelectProductOptionModal
        opened={optionModalOpened}
        onClose={() => {
          setOptionModalOpened(false);
          setOptionModalProduct(null);
        }}
        product={optionModalProduct}
        isExpress={isExpress}
        onConfirm={(payload) => handleOptionConfirm(payload)}
      />
      <NewProductModal
        opened={openedAddProductModal}
        onClose={() => setOpenedAddProductModal(false)}
        afterSubmit={(product: IProduct) => {
          setOpenedAddProductModal(false);
          refetchSections?.();
        }}
      />
      <Grid>
        <Grid.Col span={12}>
          <SectionsTabs
            activeTab={params.get('section')}
            setActiveTab={updateTab}
            sections={sections || []}
            loading={isLoadingSections}>
            <Grid>
              <Grid.Col span={12}>{sectionsPanels}</Grid.Col>
            </Grid>
          </SectionsTabs>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default ProductList;
